/**
 * The composer's menus as data, shared by web and native and free of React
 * so it can be tested on its own:
 *
 *  - which menu is open for a draft (`resolveComposerMenu`): the "+" sources
 *    menu, the "@" mentions or the "/" commands;
 *  - the rows of the sources menus (`composerSourceRows`): the web "+" menu
 *    lists "Add photos and files", Web search, Create an image and Result
 *    format, each capability row stepping through its states on click;
 *    typing "@" lists "Add photos and files" and the mentions Web search,
 *    Image, Document, Slides and Page; the phone's "+" sheet lists the same
 *    mentions as tool rows (each puts a chip on the next message), then
 *    More uploads.
 *
 * Capability rows only appear where the controls apply: seeker threads, and
 * not while the draft is a slash command. A plan-locked Image stays on the
 * list with a lock and opens the upgrade modal.
 */
import type { AgentCapabilityControls } from '@/lib/agent-capabilities-preference';
import {
  parseComposerToken,
  type CapabilityMentionKey,
  type ComposerToken,
} from '@/lib/agent-capabilities-mentions';

/** plus: the web "+" menu. mention: the "@" list. sheet: the phone's "+" sheet. */
export type ComposerSourcesContext = 'plus' | 'mention' | 'sheet';

export type ComposerSourceKey =
  | 'attach'
  | 'research'
  | 'image'
  | 'result'
  | 'document'
  | 'slides'
  | 'page';

export type ComposerSourceRow = {
  key: ComposerSourceKey;
  /**
   * attach: opens the file picker. cycle: moves the sticky control to its
   * next state. mention: writes the mention into the draft. upgrade: a
   * plan-locked Image, opens the upgrade modal.
   */
  action: 'attach' | 'cycle' | 'mention' | 'upgrade';
  /** Set on every "@" row: the mention it writes (or, locked, would write). */
  mention?: CapabilityMentionKey;
};

/** The rows for one menu, in display order. */
export function composerSourceRows(
  controls: AgentCapabilityControls,
  context: ComposerSourcesContext,
  capabilitiesEnabled: boolean,
): ComposerSourceRow[] {
  if (context === 'sheet') {
    // The tools first, then More uploads (the file picker) last; the photos
    // have their own tiles above the rows.
    const tools = capabilitiesEnabled
      ? composerSourceRows(controls, 'mention', true).filter((row) => row.key !== 'attach')
      : [];
    return [...tools, { key: 'attach', action: 'attach' }];
  }
  const rows: ComposerSourceRow[] = [{ key: 'attach', action: 'attach' }];
  if (!capabilitiesEnabled) return rows;

  if (context === 'plus') {
    if (controls.research.visible) rows.push({ key: 'research', action: 'cycle' });
    if (controls.image.visible) {
      rows.push({ key: 'image', action: controls.image.locked ? 'upgrade' : 'cycle' });
    }
    if (controls.artifact.visible) rows.push({ key: 'result', action: 'cycle' });
    return rows;
  }

  if (controls.research.visible) {
    rows.push({ key: 'research', action: 'mention', mention: 'research' });
  }
  // An image mention asks for an image result, so it needs both.
  if (controls.image.visible && controls.artifact.visible) {
    rows.push({
      key: 'image',
      action: controls.image.locked ? 'upgrade' : 'mention',
      mention: 'image',
    });
  }
  if (controls.artifact.visible) {
    rows.push({ key: 'document', action: 'mention', mention: 'document' });
    rows.push({ key: 'slides', action: 'mention', mention: 'slides' });
    rows.push({ key: 'page', action: 'mention', mention: 'page' });
  }
  return rows;
}

/** The next state a click moves a control to, wrapping at the end. */
export function nextComposerMode<T extends string>(
  current: T,
  options: readonly T[],
): T {
  const index = options.indexOf(current);
  return options[(index + 1) % options.length] ?? current;
}

/** Rows whose title contains the query, any case. */
export function filterComposerRows<T>(
  rows: readonly T[],
  query: string,
  titleFor: (row: T) => string,
): T[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [...rows];
  return rows.filter((row) => titleFor(row).toLocaleLowerCase().includes(needle));
}

export type ComposerMenuKind = 'sources' | 'mentions' | 'commands';

/**
 * The menu a draft opens. The "+" menu wins while it is open. Escape (or a
 * pointer-down outside) dismisses the menu of the token being typed until the
 * next keystroke, which clears `dismissed`. Employer threads have neither
 * mentions nor commands; mentions also need the capability controls, so a
 * slash command draft never opens them.
 */
export function resolveComposerMenu(params: {
  text: string;
  plusOpen: boolean;
  dismissed: boolean;
  employer: boolean;
  capabilitiesEnabled: boolean;
}): { kind: ComposerMenuKind | null; token: ComposerToken | null } {
  if (params.plusOpen) return { kind: 'sources', token: null };
  if (params.dismissed || params.employer) return { kind: null, token: null };
  const token = parseComposerToken(params.text, {
    mentions: params.capabilitiesEnabled,
  });
  if (!token) return { kind: null, token: null };
  return { kind: token.kind === 'mention' ? 'mentions' : 'commands', token };
}
